import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Button,
  Form,
  FormControl,
  Row,
  Col,
  Table,
  InputGroup,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { Search as FiSearch, Trash3 as FiTrash2 } from 'react-bootstrap-icons';
import {
  Client,
  getClientById,
  searchSalesPeople,
  SalesPerson,
} from '../../api/clients';
import {
  createReturnServer,
  getApprovedSalesLinesServer,
} from '../../api/returnsServer';
import { listAccountsReceivable } from '../../api/accountsReceivable';
import {
  loadConfigurationFactors,
  shouldQueueProfitProductInventoryRefreshFromProfit,
} from '../../api/configuration';
import { listExchangeSourcesForPair } from '../../api/exchangeSources';
import { getCurrentAuthUser } from '../../utils/currentAuthUser';
import { formatExchangeRate } from '../../utils/formatExchangeRate';
import { useLoading } from '../../hooks/useLoading';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { SUPPORTED_CURRENCIES } from '../../models/organization';
import {
  ReturnCreate,
  ReturnProduct,
  ApprovedSaleLine,
} from '../../models/return';
import type { AccountReceivable } from '../../models/accountsReceivable';
import type { ProfitPlus2k8OrgConfig } from '../../models/configuration';
import type { ExchangeSourceOption } from '../../models/exchangeSource';
import formStyles from '../orders/OrderForm.module.scss';
import { formatSalesPersonOptionLabel } from '../orders/orderForm/orderFormSalesPerson';

interface ReturnFormProps {
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
  organizationId: string;
  addToast: (message: string) => void;
}

const mapReturnCreateError = (error: any): string => {
  const detail = String(
    error?.response?.data?.detail || error?.message || ''
  ).trim();
  const normalized = detail.toLowerCase();
  if (normalized.includes('source sale not found')) {
    return 'No se encontró la venta origen de una de las líneas seleccionadas.';
  }
  if (normalized.includes('not approved')) {
    return 'La venta origen debe estar aprobada para poder devolver sus productos.';
  }
  if (normalized.includes('client does not match')) {
    return 'No se pueden mezclar líneas de clientes distintos en una misma devolución.';
  }
  if (normalized.includes('exceeds returnable quantity')) {
    return 'La cantidad a devolver supera la cantidad disponible para esa línea.';
  }
  if (normalized.includes('client credit was already used or refunded')) {
    return 'No se puede completar la operación porque el saldo a favor relacionado ya fue usado o reembolsado.';
  }
  return detail || 'Error al crear la devolución';
};

interface SelectedProduct extends ReturnProduct {
  id: string;
  quantity: number;
  selectedQuantity: number;
  maxReturnableQuantity: number;
  sourceCreatedAt?: Date;
  /** For UI grouping; from the approved sale line */
  sourceClientName?: string;
}

interface TaxConfigItem {
  id: string;
  name: string;
  rate: number;
  enabled: boolean;
}

const ReturnForm: React.FC<ReturnFormProps> = ({
  show,
  onClose,
  onSaved,
  organizationId,
  addToast,
}) => {
  const { showLoading, hideLoading } = useLoading();
  const { showInfoConfirmation } = useConfirmationDialog();
  const [step, setStep] = useState<number>(1);
  const [quantityDraftByProductId, setQuantityDraftByProductId] = useState<
    Record<string, string>
  >({});

  // Step 1: Products
  const [saleLines, setSaleLines] = useState<ApprovedSaleLine[]>([]);
  const [saleLineSearch, setSaleLineSearch] = useState('');
  const [productPending, setProductPending] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientReceivables, setClientReceivables] = useState<
    AccountReceivable[]
  >([]);

  // Step 3: Salesperson and payment
  const [salesPeople, setSalesPeople] = useState<SalesPerson[]>([]);
  const [vendorPending, setVendorPending] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [paymentCurrency, setPaymentCurrency] = useState<string>('');
  const [comment, setComment] = useState('');
  const [originalOrderId, setOriginalOrderId] = useState<string>('');
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<
    Array<{ id: number; name: string; enabled: boolean }>
  >([]);
  const [currency, setCurrency] = useState<string>('VES');
  const [referenceCurrency, setReferenceCurrency] = useState<string>('USD');

  // Configuration factors for IVA calculation and approval settings
  const [configurationFactors, setConfigurationFactors] = useState<{
    iva: number;
    orderApprovalRequired: boolean;
    taxes: TaxConfigItem[];
    primaryTaxId: string;
  }>({ iva: 16, orderApprovalRequired: false, taxes: [], primaryTaxId: '' });

  // Exchange rate from configuration factors
  const [exchangeRate, setExchangeRate] = useState<number>(36.0);
  const [defaultExchangeRate, setDefaultExchangeRate] = useState<number>(36.0);
  const [defaultExchangeSourceId, setDefaultExchangeSourceId] =
    useState<string>('');
  const [exchangeSourceOverrideId, setExchangeSourceOverrideId] =
    useState<string>('');
  const [exchangeSources, setExchangeSources] = useState<
    ExchangeSourceOption[]
  >([]);
  const [profitPlus2k8OrgConfig, setProfitPlus2k8OrgConfig] =
    useState<ProfitPlus2k8OrgConfig | null>(null);

  const groupedNumberFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const getCurrencySymbol = useCallback((code?: string): string => {
    if (!code) return '';
    const match = SUPPORTED_CURRENCIES.find(curr => curr.code === code);
    return match?.symbol || code;
  }, []);

  const formatMoney = useCallback(
    (amount: number, code?: string): string => {
      const rounded = groupedNumberFormatter.format(Number(amount) || 0);
      const symbol = getCurrencySymbol(code);
      return symbol ? `${symbol}\u00A0${rounded}` : `${rounded}`;
    },
    [getCurrencySymbol, groupedNumberFormatter]
  );

  const formatNumber = useCallback(
    (value: number): string => {
      return groupedNumberFormatter.format(Number(value) || 0);
    },
    [groupedNumberFormatter]
  );

  const formatSaleGroupDate = useCallback((d?: Date): string => {
    if (!d) return '';
    const t = d.getTime();
    if (Number.isNaN(t)) return '';
    return d.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }, []);

  const getLineSubtotal = useCallback((product: SelectedProduct): number => {
    const unitPrice = Number(product.price) || 0;
    const quantity = Number(product.selectedQuantity) || 0;
    const discount = Math.min(100, Math.max(0, Number(product.discount) || 0));
    return unitPrice * quantity * (1 - discount / 100);
  }, []);

  // Derived totals with per-product tax calculation
  const totals = useMemo(() => {
    const subtotal = selectedProducts.reduce(
      (acc, p) => acc + getLineSubtotal(p),
      0
    );
    const ivaAmount = selectedProducts.reduce((acc, p) => {
      const taxRate = Number(p.taxRate) || 0;
      return acc + getLineSubtotal(p) * (taxRate / 100);
    }, 0);
    const total = subtotal + ivaAmount;
    return { subtotal, ivaAmount, total };
  }, [selectedProducts, getLineSubtotal]);

  const fetchApprovedSaleLines = useCallback(async () => {
    setProductPending(true);
    try {
      const result = await getApprovedSalesLinesServer(organizationId, {
        clientId: selectedClient?.id || undefined,
        searchTerm: saleLineSearch || undefined,
        limit: 200,
        offset: 0,
      });
      setSaleLines(result.items || []);
    } catch (e) {
      console.error('Failed to load approved sale lines:', e);
      addToast('Error al cargar líneas de ventas aprobadas');
    } finally {
      setProductPending(false);
    }
  }, [organizationId, saleLineSearch, selectedClient?.id, addToast]);

  const fetchVendors = useCallback(async () => {
    setVendorPending(true);
    try {
      const salesPeopleList = await searchSalesPeople(organizationId);

      // Add current admin as a vendor option
      const currentUser = getCurrentAuthUser();
      if (currentUser) {
        const adminSalesPerson: SalesPerson = {
          id: 'admin',
          email: currentUser.email || undefined,
          name: currentUser.displayName || 'Administrador',
          created: new Date(),
        };

        // Add admin as first option
        setSalesPeople([adminSalesPerson, ...(salesPeopleList || [])]);
      } else {
        setSalesPeople(salesPeopleList || []);
      }
    } catch (e) {
      console.error('Failed to load vendors:', e);
    } finally {
      setVendorPending(false);
    }
  }, [organizationId]);

  /**
   * `creatorSalesPerson` on the return is persisted as email, código, nombre o id (matching how
   * orders store it). The vendedor `<select>` uses `SalesPerson.id`, so resolve it on submit
   * to a stable string that downstream tooling (Profit, reportes) can match.
   */
  const resolveCreatorSalesPersonString = useCallback((): string => {
    if (selectedVendorId === 'admin') {
      return getCurrentAuthUser()?.email?.trim() || 'Administrador';
    }
    const sp = salesPeople.find(s => s.id === selectedVendorId);
    if (!sp) return selectedVendorId;
    return (
      sp.email?.trim() ||
      sp.code?.trim() ||
      sp.name?.trim() ||
      sp.id
    );
  }, [selectedVendorId, salesPeople]);

  const fetchConfigurationFactors = useCallback(async () => {
    try {
      const config = await loadConfigurationFactors(organizationId);
      const paymentMethods = config.paymentMethodsAvailable || [];

      setAvailablePaymentMethods(paymentMethods);

      // Set default payment method to first enabled method
      const firstEnabled = paymentMethods.find(pm => pm.enabled);
      if (firstEnabled) {
        setPaymentMethod(firstEnabled.name);
      }

      // Set currency and referenceCurrency
      setCurrency(config.currency || 'VES');
      setReferenceCurrency(config.referenceCurrency || 'USD');

      // Set default paymentCurrency to currency
      setPaymentCurrency(config.currency || 'VES');

      setConfigurationFactors({
        iva: config.iva || 16,
        orderApprovalRequired: config.orderApprovalRequired || false,
        taxes: config.taxes || [],
        primaryTaxId: config.primaryTaxId || '',
      });
      // Also set exchange rate from configuration
      if (config.exchangeRate) {
        setExchangeRate(config.exchangeRate);
        setDefaultExchangeRate(config.exchangeRate);
      }
      setDefaultExchangeSourceId(
        config.useAutomaticExchangeRate ? config.exchangeSourceId || '' : ''
      );
      setProfitPlus2k8OrgConfig(config.profitPlus2k8 ?? null);
    } catch (e) {
      console.error('Failed to load configuration factors:', e);
      setProfitPlus2k8OrgConfig(null);
    }
  }, [organizationId]);

  // Load exchange sources for referenceCurrency -> currency (for per-return overrides)
  useEffect(() => {
    const loadSources = async () => {
      if (!referenceCurrency || !currency) {
        setExchangeSources([]);
        return;
      }
      try {
        const sources = await listExchangeSourcesForPair({
          fromCurrency: referenceCurrency,
          toCurrency: currency,
        });
        const mapped: ExchangeSourceOption[] = sources.map(s => ({
          id: s.id,
          name: s.name,
          rate: s.rate,
          sourceUrl: s.sourceUrl,
          lastFetchedAt: s.lastFetchedAt,
        }));
        setExchangeSources(mapped);
      } catch (e) {
        console.warn('Failed to load exchange sources', e);
        setExchangeSources([]);
      }
    };
    loadSources();
  }, [currency, referenceCurrency]);

  // Load original order if orderId is provided (simplified - just store the ID)
  // Note: Full order loading would require a getOrderById API endpoint
  // For now, users can manually enter the order ID as reference

  // Reset form and load data only when modal opens (show becomes true).
  // Do NOT include fetch callbacks in deps: they change when search state changes
  // (e.g. clientSearch) and would re-run this effect and reset step to 1.
  useEffect(() => {
    if (show) {
      setStep(1);
      setQuantityDraftByProductId({});
      setSelectedProducts([]);
      setSelectedClient(null);
      setClientReceivables([]);
      setSelectedVendorId('');
      setPaymentMethod('');
      setPaymentCurrency('');
      setComment('');
      setOriginalOrderId('');
      fetchApprovedSaleLines();
      fetchVendors();
      fetchConfigurationFactors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on show change
  }, [show]);

  // Fetch approved sale lines when search/client changes
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        fetchApprovedSaleLines();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [saleLineSearch, show, selectedClient?.id, fetchApprovedSaleLines]);

  // Full client record from Firestore (name from sale line is not enough for RIF/phone/email).
  useEffect(() => {
    const clientId = selectedClient?.id;
    if (!show || !clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const full = await getClientById(organizationId, clientId);
        if (cancelled || !full || full.id !== clientId) return;
        setSelectedClient(full);
      } catch (e) {
        console.warn('ReturnForm: could not load client', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, organizationId, selectedClient?.id]);

  // Allow picking a different client again after the user removes all lines
  useEffect(() => {
    if (!show || selectedProducts.length > 0) return;
    setSelectedClient(null);
  }, [show, selectedProducts.length]);

  useEffect(() => {
    const clientId = selectedClient?.id;
    if (!show || !clientId) {
      setClientReceivables([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await listAccountsReceivable(organizationId, {
          clientId,
        });
        if (!cancelled) {
          setClientReceivables(result.items || []);
        }
      } catch (e) {
        console.warn('ReturnForm: could not load client receivables', e);
        if (!cancelled) {
          setClientReceivables([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, organizationId, selectedClient?.id]);

  const addProduct = (line: ApprovedSaleLine) => {
    const compositeId = `${line.saleId}:${line.sourceSaleProductId}`;
    if (selectedProducts.find(sp => sp.id === compositeId)) return;
    if (selectedClient && selectedClient.id !== line.clientId) {
      addToast(
        'No se pueden mezclar líneas de clientes distintos en la misma devolución'
      );
      return;
    }

    const seedClient: Client = {
      id: line.clientId,
      rif: '',
      businessName: line.clientName,
      legalName: line.clientName,
    } as any;
    if (!selectedClient) {
      setSelectedClient(seedClient);
    }

    setSelectedProducts(prev =>
      prev.concat({
        id: compositeId,
        productId: line.productId || line.sourceSaleProductId,
        code: line.code,
        name: line.name,
        brand: line.brand,
        description: line.description,
        price: line.price,
        discount: line.discount,
        quantity: 1,
        selectedQuantity: 1,
        maxReturnableQuantity: Number(line.quantityReturnable) || 0,
        imageUrl: line.imageUrl,
        taxId: line.taxId,
        taxRate: line.taxRate,
        sourceSaleId: line.saleId,
        sourceSaleNumber: line.saleNumber,
        sourceSaleProductId: line.sourceSaleProductId,
        sourceCreatedAt: line.created
          ? new Date(line.created as any)
          : undefined,
        sourceClientName: line.clientName,
        warehouseId: line.warehouseId,
      } as SelectedProduct)
    );
  };

  const removeProduct = (id: string) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== id));
    setQuantityDraftByProductId(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const commitQuantity = useCallback((id: string, raw: number) => {
    if (!Number.isFinite(raw) || raw <= 0) return;
    setSelectedProducts(prev =>
      prev.map(p => (p.id === id ? { ...p, selectedQuantity: raw } : p))
    );
  }, []);

  const canProceedStep1 = selectedProducts.length > 0;
  const selectedSaleIds = useMemo(
    () =>
      Array.from(
        new Set(selectedProducts.map(p => p.sourceSaleId).filter(Boolean))
      ),
    [selectedProducts]
  );

  const approvedSaleLineGroups = useMemo(() => {
    const bySale = new Map<
      string,
      { header: ApprovedSaleLine; lines: ApprovedSaleLine[] }
    >();
    for (const line of saleLines) {
      let g = bySale.get(line.saleId);
      if (!g) {
        g = { header: line, lines: [] };
        bySale.set(line.saleId, g);
      }
      g.lines.push(line);
    }
    return Array.from(bySale.values())
      .map(g => ({ ...g, lines: [...g.lines] }))
      .sort((a, b) => {
        const ta = a.header.created
          ? new Date(a.header.created as any).getTime()
          : 0;
        const tb = b.header.created
          ? new Date(b.header.created as any).getTime()
          : 0;
        if (tb !== ta) return tb - ta;
        return (b.header.saleNumber || 0) - (a.header.saleNumber || 0);
      });
  }, [saleLines]);

  const selectedReturnLineGroups = useMemo(() => {
    const bySale = new Map<string, SelectedProduct[]>();
    for (const p of selectedProducts) {
      const sid = String(p.sourceSaleId || '');
      if (!bySale.has(sid)) bySale.set(sid, []);
      bySale.get(sid)!.push(p);
    }
    return Array.from(bySale.entries())
      .map(([saleId, products]) => ({
        saleId,
        products,
        saleNumber: products[0]?.sourceSaleNumber,
        clientName: products[0]?.sourceClientName,
        created: products[0]?.sourceCreatedAt,
      }))
      .sort((a, b) => {
        const ta = a.created ? a.created.getTime() : 0;
        const tb = b.created ? b.created.getTime() : 0;
        if (tb !== ta) return tb - ta;
        return (b.saleNumber || 0) - (a.saleNumber || 0);
      });
  }, [selectedProducts]);
  const cxcSplitPreview = useMemo(() => {
    const receivableBySaleId = new Map(
      clientReceivables.map(item => [String(item.saleId || item.id), item])
    );
    const bySale = new Map<
      string,
      {
        saleId: string;
        saleNumber?: number;
        returnAmount: number;
        receivableBalance: number;
        closeAmount: number;
        excessCreditAmount: number;
        currency: string;
      }
    >();
    selectedProducts.forEach(product => {
      const saleId = String(product.sourceSaleId || '').trim();
      if (!saleId) return;
      const receivable = receivableBySaleId.get(saleId);
      const current = bySale.get(saleId) || {
        saleId,
        saleNumber: product.sourceSaleNumber,
        returnAmount: 0,
        receivableBalance: Number(receivable?.balanceAmount || 0),
        closeAmount: 0,
        excessCreditAmount: 0,
        currency:
          receivable?.currency || referenceCurrency || currency || 'USD',
      };
      const taxRate = Number(product.taxRate) || 0;
      const lineSubtotal = getLineSubtotal(product);
      current.returnAmount += lineSubtotal * (1 + taxRate / 100);
      current.closeAmount = Math.min(
        current.returnAmount,
        current.receivableBalance
      );
      current.excessCreditAmount = Math.max(
        0,
        current.returnAmount - current.closeAmount
      );
      bySale.set(saleId, current);
    });
    return Array.from(bySale.values());
  }, [
    clientReceivables,
    currency,
    getLineSubtotal,
    referenceCurrency,
    selectedProducts,
  ]);
  const hasClientCreditPreview = cxcSplitPreview.some(
    item => item.excessCreditAmount > 0
  );

  const handleSubmit = async () => {
    if (!selectedClient || selectedProducts.length === 0 || !selectedVendorId)
      return;

    const invalidQuantity = selectedProducts.some(p => {
      const q = Number(p.selectedQuantity);
      return (
        !Number.isFinite(q) ||
        q <= 0 ||
        q > (Number(p.maxReturnableQuantity) || 0)
      );
    });
    if (invalidQuantity) {
      addToast(
        'Cada línea debe tener cantidad válida y no mayor al máximo retornable.'
      );
      return;
    }

    showLoading('Creando devolución...', 'overlay');
    try {
      const payload: ReturnCreate = {
        originalOrderId:
          selectedSaleIds.length === 1
            ? selectedSaleIds[0]
            : originalOrderId || undefined,
        clientId: selectedClient.id,
        clientName:
          selectedClient.businessName ||
          selectedClient.legalName ||
          selectedClient.rif,
        creatorSalesPerson: resolveCreatorSalesPersonString(),
        paymentMethod: paymentMethod || '',
        paymentCurrency: paymentCurrency || '',
        subtotal: totals.subtotal,
        ivaAmount: totals.ivaAmount > 0 ? totals.ivaAmount : undefined,
        total: totals.total,
        exchangeRate: exchangeRate,
        deleted: false,
        comment: comment || '',
        products: selectedProducts.map(p => {
          return {
            productId: p.productId,
            code: p.code,
            name: p.name,
            brand: p.brand,
            description: p.description,
            price: Number(p.price) || 0,
            quantity: Number(p.selectedQuantity) || 0,
            discount: Math.min(100, Math.max(0, Number(p.discount) || 0)),
            imageUrl: p.imageUrl,
            taxId: p.taxId,
            taxRate: Number(p.taxRate) || 0,
            sourceSaleId: p.sourceSaleId,
            sourceSaleNumber: p.sourceSaleNumber,
            sourceSaleProductId: p.sourceSaleProductId,
            ...(p.warehouseId ? { warehouseId: p.warehouseId } : {}),
          };
        }),
      };

      await createReturnServer(
        organizationId,
        payload,
        configurationFactors.orderApprovalRequired
      );
      hideLoading();
      onSaved();
      onClose();
    } catch (e) {
      console.log(e);
      hideLoading();
      addToast(mapReturnCreateError(e));
    }
  };

  const handleCreateReturn = () => {
    showInfoConfirmation(
      'Crear Devolución',
      '¿Estás seguro de que quieres crear esta devolución?',
      handleSubmit,
      {
        confirmText: 'Crear Devolución',
        cancelText: 'Cancelar',
      }
    );
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Crear Devolución</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        {shouldQueueProfitProductInventoryRefreshFromProfit(
          profitPlus2k8OrgConfig
        ) && (
          <Alert variant="info" className="small">
            <strong>Profit Plus 2k8:</strong> registre la devolución primero en
            Profit y luego emúlela aquí. La actualización de inventario desde
            Profit se encola al aprobar la devolución (con aprobación
            automática, al guardar). Una versión futura podría automatizar este
            flujo.
          </Alert>
        )}
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant={step === 1 ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setStep(1)}
            >
              1. Productos
            </Button>
            <Button
              type="button"
              variant={step === 2 ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setStep(2)}
              disabled={!canProceedStep1}
            >
              2. Finalizar
            </Button>
          </div>
        </div>

        {step === 1 && (
          <div className={formStyles.returnStepOneSplit}>
            <div
              className={`${formStyles.returnStepOnePane} ${formStyles.returnStepOnePaneLeft}`}
            >
              <div className={`${formStyles.stepThreeSection} h-100`}>
                <div className={formStyles.stepThreeSectionHeader}>
                  <h6 className="mb-0">Seleccionar líneas de ventas aprobadas</h6>
                </div>
                <div
                  className={formStyles.stepThreeSectionBody}
                  style={{ maxHeight: '56vh', overflowY: 'auto' }}
                >
                  {selectedClient && selectedProducts.length > 0 && (
                    <Alert variant="light" className="py-2 border small mb-3">
                      Mostrando solo ventas aprobadas de{' '}
                      <strong>
                        {selectedClient.businessName ||
                          selectedClient.legalName ||
                          selectedClient.rif ||
                          'este cliente'}
                      </strong>
                      . Puede seguir agregando líneas de otras ventas de este
                      mismo cliente.
                    </Alert>
                  )}

                  <Form
                    role="search"
                    aria-label="Buscar ventas y productos"
                    onSubmit={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      fetchApprovedSaleLines();
                    }}
                    className="mb-3"
                  >
                    <InputGroup>
                      <InputGroup.Text>
                        <FiSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="search"
                        placeholder="Nº de venta, cliente, ID de venta, código o nombre de producto…"
                        value={saleLineSearch}
                        aria-label="Buscar ventas y líneas"
                        autoComplete="off"
                        onChange={e => setSaleLineSearch(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            fetchApprovedSaleLines();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline-primary"
                        onClick={fetchApprovedSaleLines}
                        disabled={productPending}
                        aria-busy={productPending}
                        aria-label="Buscar productos"
                      >
                        {productPending ? (
                          <Spinner animation="border" size="sm" aria-hidden />
                        ) : (
                          <FiSearch />
                        )}
                      </Button>
                    </InputGroup>
                  </Form>

                  {productPending ? (
                    <div className="d-flex align-items-center gap-2 py-2">
                      <Spinner animation="border" size="sm" />
                      <span className="text-muted small">
                        Buscando ventas y líneas...
                      </span>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table
                        hover
                        size="sm"
                        className={`${formStyles.productsSummaryTable} ${formStyles.returnCompactTable} ${formStyles.fixedActionColumnTable}`}
                      >
                        <thead>
                          <tr>
                            <th>Venta / Línea</th>
                            <th>Código</th>
                            <th>Nombre</th>
                            <th>Cant. vendida</th>
                            <th>Cant. retornable</th>
                            <th>Precio</th>
                            <th>Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {approvedSaleLineGroups.length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="text-muted text-center py-4"
                              >
                                {selectedProducts.length === 0
                                  ? 'Busque por número de venta, nombre de cliente o producto. Las ventas aparecen agrupadas; al agregar la primera línea, solo se listan ventas aprobadas del mismo cliente.'
                                  : 'No hay líneas que coincidan. Pruebe otro criterio de búsqueda.'}
                              </td>
                            </tr>
                          ) : (
                            approvedSaleLineGroups.map(group => (
                              <React.Fragment key={group.header.saleId}>
                                <tr className={formStyles.returnGroupHeaderRow}>
                                  <td colSpan={7} className="small">
                                    <strong>
                                      Venta #
                                      {group.header.saleNumber != null
                                        ? group.header.saleNumber
                                        : group.header.saleId.slice(0, 8)}
                                    </strong>
                                    <span
                                      className={`${formStyles.returnGroupClientName} ms-2`}
                                    >
                                      {group.header.clientName}
                                    </span>
                                    {(() => {
                                      const dateLabel = formatSaleGroupDate(
                                        group.header.created
                                          ? new Date(group.header.created as any)
                                          : undefined
                                      );
                                      return dateLabel ? (
                                        <span
                                          className={`${formStyles.returnGroupDate} ms-2`}
                                        >
                                          · {dateLabel}
                                        </span>
                                      ) : null;
                                    })()}
                                  </td>
                                </tr>
                                {group.lines.map(line => (
                                  <tr
                                    key={`${line.saleId}:${line.sourceSaleProductId}`}
                                  >
                                    <td className="text-muted small ps-3">
                                      Venta #
                                      {line.saleNumber ??
                                        line.saleId.slice(0, 8)}
                                    </td>
                                    <td>{line.code}</td>
                                    <td>{line.name}</td>
                                    <td>{formatNumber(line.quantitySold)}</td>
                                    <td>
                                      {formatNumber(line.quantityReturnable)}
                                    </td>
                                    <td>
                                      {formatMoney(
                                        Number(line.price) || 0,
                                        referenceCurrency || currency
                                      )}
                                    </td>
                                    <td>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => addProduct(line)}
                                        disabled={selectedProducts.some(
                                          sp =>
                                            sp.id ===
                                            `${line.saleId}:${line.sourceSaleProductId}`
                                        )}
                                      >
                                        Agregar
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              className={`${formStyles.returnStepOnePane} ${formStyles.returnStepOnePaneRight}`}
            >
              <div className={`${formStyles.stepThreeSection} h-100`}>
                <div className={formStyles.stepThreeSectionHeader}>
                  <h6 className="mb-0">Líneas seleccionadas</h6>
                </div>
                <div
                  className={formStyles.stepThreeSectionBody}
                  style={{ maxHeight: '56vh', overflowY: 'auto' }}
                >
                  {selectedProducts.length > 0 ? (
                    selectedReturnLineGroups.map(grp => (
                      <div key={grp.saleId || 'unknown'} className="mb-3">
                        <div className={`${formStyles.returnGroupHeaderRow} small px-2 py-1 rounded`}>
                          <strong>
                            Venta #
                            {grp.saleNumber != null
                              ? grp.saleNumber
                              : (grp.saleId || '').slice(0, 8)}
                          </strong>
                          {grp.clientName ? (
                            <span className={`${formStyles.returnGroupClientName} ms-2`}>
                              {grp.clientName}
                            </span>
                          ) : null}
                          {(() => {
                            const dl = formatSaleGroupDate(grp.created);
                            return dl ? (
                              <span className={`${formStyles.returnGroupDate} ms-2`}>
                                · {dl}
                              </span>
                            ) : null;
                          })()}
                        </div>
                        {grp.products.map(p => (
                          <div key={p.id} className="card mt-2">
                            <div className="card-body py-2 px-2">
                              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                <div>
                                  <div className="fw-semibold">{p.name}</div>
                                  <div className="small text-muted">
                                    {p.code} · Desc. {formatNumber(Number(p.discount) || 0)}%
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => removeProduct(p.id)}
                                  aria-label="Quitar producto"
                                >
                                  <FiTrash2 />
                                </Button>
                              </div>
                              <div className={formStyles.returnLineMetaGrid}>
                                <div className={formStyles.returnLineMetaItem}>
                                  <div className={formStyles.returnLineMetaLabel}>Precio</div>
                                  <div className={formStyles.returnLineMetaValue}>
                                    {formatMoney(
                                      Number(p.price) || 0,
                                      referenceCurrency || currency
                                    )}
                                  </div>
                                </div>
                                <div className={formStyles.returnLineMetaItem}>
                                  <div className={formStyles.returnLineMetaLabel}>Impuesto</div>
                                  <div className={formStyles.returnLineMetaText}>
                                    {formatNumber(Number(p.taxRate) || 0)}%
                                  </div>
                                </div>
                                <div className={formStyles.returnLineMetaItem}>
                                  <div className={formStyles.returnLineMetaLabel}>Cantidad</div>
                                  <FormControl
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    step="any"
                                    value={
                                      quantityDraftByProductId[p.id] ??
                                      String(p.selectedQuantity)
                                    }
                                    onFocus={() =>
                                      setQuantityDraftByProductId(prev => ({
                                        ...prev,
                                        [p.id]: String(p.selectedQuantity),
                                      }))
                                    }
                                    onChange={e => {
                                      const raw = e.currentTarget.value;
                                      setQuantityDraftByProductId(prev => ({
                                        ...prev,
                                        [p.id]: raw,
                                      }));
                                      const trimmed = raw.trim();
                                      if (trimmed === '') return;
                                      const n = parseFloat(trimmed);
                                      if (Number.isFinite(n) && n > 0) {
                                        commitQuantity(
                                          p.id,
                                          Math.min(
                                            n,
                                            Number(p.maxReturnableQuantity) || n
                                          )
                                        );
                                      }
                                    }}
                                    onBlur={e => {
                                      const raw = e.currentTarget.value;
                                      setQuantityDraftByProductId(prev => {
                                        const next = { ...prev };
                                        delete next[p.id];
                                        return next;
                                      });
                                      const n = parseFloat(raw.trim());
                                      if (Number.isFinite(n) && n > 0) {
                                        commitQuantity(
                                          p.id,
                                          Math.min(
                                            n,
                                            Number(p.maxReturnableQuantity) || n
                                          )
                                        );
                                      }
                                    }}
                                  />
                                  <small className={formStyles.returnLineMetaHint}>
                                    máx: {formatNumber(Number(p.maxReturnableQuantity) || 0)}
                                  </small>
                                </div>
                                <div
                                  className={`${formStyles.returnLineMetaItem} ${formStyles.returnLineMetaItemTotal}`}
                                >
                                  <div className={formStyles.returnLineMetaLabel}>Total</div>
                                  <div
                                    className={`${formStyles.returnLineMetaValue} ${formStyles.returnLineMetaTotalValue}`}
                                  >
                                    {formatMoney(
                                      getLineSubtotal(p),
                                      referenceCurrency || currency
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-muted py-3">
                      Sin líneas seleccionadas
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h5 className="mb-3">Finalizar devolución</h5>
            <Row className="mb-3 g-3">
              <Col md={5}>
                <div className={`${formStyles.stepThreeSection} h-100`}>
                  <div
                    className={`${formStyles.stepThreeSectionHeader} d-flex justify-content-between align-items-start gap-2`}
                  >
                    <h6 className="mb-0">Cliente seleccionado</h6>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setStep(1)}
                      title="Cambiar líneas"
                    >
                      Cambiar
                    </Button>
                  </div>
                  <div className={formStyles.stepThreeSectionBody}>
                    {selectedClient ? (
                      <>
                        <div className={formStyles.clientPrimaryBlock}>
                          <div className={formStyles.clientName}>
                            {selectedClient.businessName ||
                              selectedClient.legalName ||
                              'Cliente'}
                          </div>
                          <div className={formStyles.clientMeta}>
                            <span>
                              Cód.{' '}
                              <strong>
                                {(selectedClient as any).code || '—'}
                              </strong>
                            </span>
                            {selectedClient.rif ? (
                              <span>
                                RIF <strong>{selectedClient.rif}</strong>
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className={formStyles.clientQuickGrid}>
                          <div className={formStyles.clientQuickItem}>
                            <div className={formStyles.clientQuickLabel}>
                              Teléfono
                            </div>
                            <div className={formStyles.clientQuickValue}>
                              {selectedClient.mobile || '—'}
                            </div>
                          </div>
                          <div className={formStyles.clientQuickItem}>
                            <div className={formStyles.clientQuickLabel}>
                              Email
                            </div>
                            <div className={formStyles.clientQuickValue}>
                              {selectedClient.email || '—'}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-muted">No hay cliente detectado</div>
                    )}
                  </div>
                </div>
              </Col>
              <Col md={7}>
                <div className={`${formStyles.stepThreeSection} h-100`}>
                  <div
                    className={`${formStyles.stepThreeSectionHeader} d-flex justify-content-between align-items-start gap-2`}
                  >
                    <h6 className="mb-0">Productos seleccionados</h6>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setStep(1)}
                      title="Volver a seleccionar líneas"
                    >
                      Editar
                    </Button>
                  </div>
                  <div className={formStyles.stepThreeSectionBody}>
                    <div
                      className={`table-responsive ${formStyles.productsWrap}`}
                    >
                      <Table
                        hover
                        size="sm"
                        className={`mb-0 align-middle ${formStyles.productsSummaryTable} ${formStyles.returnCompactTable}`}
                      >
                        <thead>
                          <tr>
                            <th>Venta</th>
                            <th>Código</th>
                            <th>Nombre</th>
                            <th className={formStyles.moneyHeader}>Precio</th>
                            <th>Cant.</th>
                            <th className={formStyles.moneyHeader}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProducts.length > 0 ? (
                            selectedProducts.map(p => {
                              const currentPrice = Number(p.price) || 0;
                              const totalPrice = getLineSubtotal(p);
                              return (
                                <tr key={p.id}>
                                  <td>
                                    #
                                    {p.sourceSaleNumber ||
                                      p.sourceSaleId?.slice(0, 8)}
                                  </td>
                                  <td>{p.code}</td>
                                  <td>{p.name}</td>
                                  <td className={formStyles.moneyCellNoWrap}>
                                    {formatMoney(
                                      currentPrice,
                                      referenceCurrency || currency
                                    )}
                                    <small
                                      className={`text-muted d-block ${formStyles.moneyCellNoWrap}`}
                                    >
                                      Desc.{' '}
                                      {formatNumber(Number(p.discount) || 0)}%
                                    </small>
                                  </td>
                                  <td>{p.selectedQuantity}</td>
                                  <td className={formStyles.moneyCellNoWrap}>
                                    {formatMoney(
                                      totalPrice,
                                      referenceCurrency || currency
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={6}
                                className="text-center text-muted"
                              >
                                Sin productos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <div className={`${formStyles.stepThreeSection} mb-3`}>
              <div className={formStyles.stepThreeSectionHeader}>
                <h6 className="mb-0">Impacto en CxC y saldo a favor</h6>
              </div>
              <div className={formStyles.stepThreeSectionBody}>
                {hasClientCreditPreview ? (
                  <Alert variant="info" className="py-2">
                    Se cerrará la cuenta por cobrar hasta su saldo pendiente y
                    el diferencial quedará como saldo a favor del cliente.
                  </Alert>
                ) : null}
                <Table
                  hover
                  size="sm"
                  responsive
                  className={`mb-0 align-middle ${formStyles.productsSummaryTable}`}
                >
                  <thead>
                    <tr>
                      <th>Venta</th>
                      <th className={formStyles.moneyHeader}>
                        Monto devolución
                      </th>
                      <th className={formStyles.moneyHeader}>
                        Saldo CxC disponible
                      </th>
                      <th className={formStyles.moneyHeader}>Cierra CxC</th>
                      <th className={formStyles.moneyHeader}>Saldo a favor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cxcSplitPreview.map(item => (
                      <tr key={item.saleId}>
                        <td>#{item.saleNumber || item.saleId.slice(0, 8)}</td>
                        <td className={formStyles.moneyCellNoWrap}>
                          {formatMoney(item.returnAmount, item.currency)}
                        </td>
                        <td className={formStyles.moneyCellNoWrap}>
                          {formatMoney(item.receivableBalance, item.currency)}
                        </td>
                        <td className={formStyles.moneyCellNoWrap}>
                          {formatMoney(item.closeAmount, item.currency)}
                        </td>
                        <td className={formStyles.moneyCellNoWrap}>
                          {formatMoney(item.excessCreditAmount, item.currency)}
                        </td>
                      </tr>
                    ))}
                    {cxcSplitPreview.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          Sin impacto financiero calculado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className={`${formStyles.stepThreeSection} mb-3`}>
              <div className={formStyles.stepThreeSectionHeader}>
                <h6 className="mb-0">Condiciones y datos</h6>
              </div>
              <div className={formStyles.stepThreeSectionBody}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Vendedor</Form.Label>
                      <Form.Select
                        value={selectedVendorId}
                        onChange={e =>
                          setSelectedVendorId(e.currentTarget.value)
                        }
                        disabled={vendorPending}
                      >
                        <option value="">
                          {vendorPending ? 'Cargando…' : 'Seleccione un vendedor'}
                        </option>
                        {salesPeople.map(v => (
                          <option key={v.id} value={v.id}>
                            {formatSalesPersonOptionLabel(v)}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Se guarda como referencia de la devolución (correo,
                        código o nombre si no hay correo).
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Forma de Pago (Opcional)</Form.Label>
                      <Form.Select
                        value={paymentMethod}
                        onChange={e => setPaymentMethod(e.target.value)}
                      >
                        <option value="">Ninguna (Opcional)</option>
                        {availablePaymentMethods
                          .filter(pm => pm.enabled)
                          .map(pm => (
                            <option key={pm.id} value={pm.name}>
                              {pm.name}
                            </option>
                          ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Moneda de Pago (Opcional)</Form.Label>
                      <Form.Select
                        value={paymentCurrency}
                        onChange={e => setPaymentCurrency(e.target.value)}
                      >
                        <option value="">Ninguna (Opcional)</option>
                        {currency && (
                          <option value={currency}>{currency}</option>
                        )}
                        {referenceCurrency && (
                          <option value={referenceCurrency}>
                            {referenceCurrency}
                          </option>
                        )}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Text className="text-muted">
                        Precio, descuento e impuesto de cada línea reflejan la
                        venta origen y no son editables en devoluciones.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Comentarios</Form.Label>
                  <FormControl
                    as="textarea"
                    rows={3}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Comentarios adicionales..."
                  />
                </Form.Group>

                {referenceCurrency && currency ? (
                  <Form.Group className="mb-0">
                    <Form.Label>
                      Fuente de tipo de cambio (opcional, por devolución)
                    </Form.Label>
                    <Form.Select
                      value={exchangeSourceOverrideId}
                      onChange={e => {
                        const selectedId = e.currentTarget.value;
                        setExchangeSourceOverrideId(selectedId);
                        if (!selectedId) {
                          setExchangeRate(defaultExchangeRate);
                          return;
                        }
                        const src =
                          exchangeSources.find(s => s.id === selectedId) ??
                          null;
                        if (src && typeof src.rate === 'number') {
                          setExchangeRate(src.rate);
                        }
                      }}
                    >
                      <option value="">
                        Predeterminado (
                        {defaultExchangeSourceId ? 'automático' : 'manual'})
                      </option>
                      {exchangeSources.map(src => (
                        <option key={src.id} value={src.id}>
                          {(src.name && src.name.trim().length > 0
                            ? src.name
                            : src.sourceUrl || src.id) +
                            (typeof src.rate === 'number'
                              ? ` (rate: ${formatExchangeRate(src.rate)})`
                              : '')}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Te permite usar una fuente alternativa para esta
                      devolución sin cambiar la configuración global.
                    </Form.Text>
                    <div className="text-muted small mt-1">
                      {referenceCurrency}→{currency} · Tasa:{' '}
                      {formatExchangeRate(exchangeRate)}:1
                    </div>
                  </Form.Group>
                ) : null}
              </div>
            </div>

            <div className={formStyles.stepThreeSection}>
              <div className={formStyles.stepThreeSectionHeader}>
                <h6 className="mb-0">Resumen</h6>
              </div>
              <div className={formStyles.stepThreeSectionBody}>
                <div className={formStyles.totalsStrip}>
                  <div className="text-muted small">
                    {referenceCurrency && currency ? (
                      <>
                        {referenceCurrency}→{currency} · Tasa:{' '}
                        {formatExchangeRate(exchangeRate)}:1
                        {exchangeSourceOverrideId
                          ? ' (override)'
                          : defaultExchangeSourceId
                            ? ' (org)'
                            : ''}
                        {' · '}
                      </>
                    ) : null}
                    {selectedProducts.length} producto
                    {selectedProducts.length === 1 ? '' : 's'}
                  </div>
                  <div className="d-flex flex-wrap gap-3">
                    <div className={formStyles.totalItem}>
                      <strong>Total ({currency}):</strong>{' '}
                      {formatMoney(totals.total * exchangeRate, currency)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer
        style={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'var(--bs-body-bg)',
          zIndex: 1,
        }}
      >
        <div className={formStyles.modalFooterTotals}>
          <div className={formStyles.modalFooterTotalsRow}>
            <span className={formStyles.modalFooterTotalsLabel}>SubTotal</span>
            <span className={formStyles.footerCurrencyStack}>
              <span className={formStyles.modalFooterTotalsValue}>
                {formatMoney(totals.subtotal, referenceCurrency || currency)}
              </span>
              {(currency || '').toUpperCase() === 'VES' &&
                (referenceCurrency || '').toUpperCase() !== 'VES' && (
                  <span className={formStyles.footerCurrencySecondary}>
                    {formatMoney(totals.subtotal * exchangeRate, currency)}
                  </span>
                )}
            </span>
          </div>
          <div className={formStyles.modalFooterTotalsRow}>
            <span className={formStyles.modalFooterTotalsLabel}>Impuestos</span>
            <span className={formStyles.footerCurrencyStack}>
              <span className={formStyles.modalFooterTotalsValue}>
                {formatMoney(totals.ivaAmount, referenceCurrency || currency)}
              </span>
              {(currency || '').toUpperCase() === 'VES' &&
                (referenceCurrency || '').toUpperCase() !== 'VES' && (
                  <span className={formStyles.footerCurrencySecondary}>
                    {formatMoney(totals.ivaAmount * exchangeRate, currency)}
                  </span>
                )}
            </span>
          </div>
          <div className={formStyles.modalFooterTotalsRow}>
            <span className={formStyles.modalFooterTotalsLabel}>Total</span>
            <span className={formStyles.footerCurrencyStack}>
              <span className={`${formStyles.modalFooterTotalsValue} fw-bold`}>
                {formatMoney(totals.total, referenceCurrency || currency)}
              </span>
              {(currency || '').toUpperCase() === 'VES' &&
                (referenceCurrency || '').toUpperCase() !== 'VES' && (
                  <span className={formStyles.footerCurrencySecondary}>
                    {formatMoney(totals.total * exchangeRate, currency)}
                  </span>
                )}
            </span>
          </div>
        </div>
        <div className={formStyles.modalFooterActions}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          {step > 1 && (
            <Button
              type="button"
              variant="outline-primary"
              onClick={() => setStep(step - 1)}
            >
              Anterior
            </Button>
          )}
          {step < 2 && (
            <Button
              type="button"
              variant="primary"
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !canProceedStep1}
            >
              Siguiente
            </Button>
          )}
          {step === 2 && (
            <Button
              type="button"
              variant="primary"
              onClick={handleCreateReturn}
              disabled={!selectedVendorId}
            >
              Crear Devolución
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ReturnForm;
